import EDatePicker from '@/components/everridge/EDatePicker';
import ETextAreaInput from '@/components/everridge/ETextAreaInput';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogClose,
    DialogDescription,
} from '@/components/ui/dialog';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ReservationRequestDto } from '@/features/reservations/types/reservations-dto';
import ECheckboxWithTextInput from '@/components/everridge/ECheckboxInput';
import ETextInput from '@/components/everridge/ETextInput';
import { cn } from '@/lib/utils';
import { ReservationFormSchema } from '@/features/reservations/validators/ReservationValidator';
import ApartmentsLookup from '@/features/apartments/components/ApartmentsLookup';
import { useApartmentDetailsApi } from '@/features/apartments/hooks/useApartmentDetailsApi';
import { differenceInDays } from 'date-fns';
import InformationIconSolid from '@/components/icons/InformationIconSolid';
import DoorIcon from '@/components/icons/DoorIcon';
import { useReservationDetailsApi } from '@/features/reservations/hooks/useReservationDetailsApi';
import Cancel_01_Icon from '@/components/icons/Cancel_01_Icon';
import useMediaQuery from '@/hooks/useMediaQuery';
import { AppConfig } from '@/config/app.config';
import { DateTimeHelper } from '@/utils/dateTimeHelper';

type Props = {
    apartmentId?: string;
    reservationId?: string;
    onClose: () => void;
};

type FormData = z.infer<typeof ReservationFormSchema>;

const ReservationManage: FC<Props> = ({ apartmentId, reservationId, onClose }) => {
    const [isOpen, setIsOpen] = useState(true);

    const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);
    const { t } = useTranslation();

    const { control, handleSubmit, setValue, watch, reset } = useForm<FormData>({
        resolver: zodResolver(ReservationFormSchema),
        defaultValues: {
            reservationForSomeoneElse: false,
        },
    });

    const { apartmentDetailsData } = useApartmentDetailsApi({
        id: apartmentId,
    });

    const { reservationDetailsData, createReservationData, updateReservationData } =
        useReservationDetailsApi({ id: reservationId });

    useEffect(() => {
        if (reservationId && reservationDetailsData) {
            reset({
                apartmentId: [
                    {
                        id: reservationDetailsData.apartmentId,
                        name: reservationDetailsData.apartmentName,
                    },
                ],
                checkInDate:
                    reservationDetailsData.checkInDate &&
                    new Date(reservationDetailsData.checkInDate),
                checkOutDate:
                    reservationDetailsData.checkOutDate &&
                    new Date(reservationDetailsData.checkOutDate),
                reservationForSomeoneElse: reservationDetailsData.reservationForSomeoneElse,
                guestFullName: reservationDetailsData.guestFullName,
                remarks: reservationDetailsData.remarks,
            });
        } else if (apartmentDetailsData) {
            reset({
                apartmentId: [
                    {
                        id: apartmentDetailsData.id,
                        name: apartmentDetailsData.name,
                    },
                ],
            });
        }
    }, [apartmentDetailsData, reservationDetailsData, reservationId, reset]);

    const handleFormSubmit = async (form: FormData) => {
        const apartmentId = form.apartmentId[0].id;

        const dto: ReservationRequestDto = {
            apartmentId: apartmentId,
            checkInDate: DateTimeHelper.getDateOnly(form.checkInDate)!,
            checkOutDate: DateTimeHelper.getDateOnly(form.checkOutDate)!,
            reservationForSomeoneElse: form.reservationForSomeoneElse,
            guestFullName: form.guestFullName,
            remarks: form.remarks,
        };

        if (!reservationId) {
            await createReservationData(dto);
        } else {
            await updateReservationData(dto);
        }
        handleClose();
    };

    const handleClose = () => {
        setIsOpen(false);
        setTimeout(onClose, 300);
        reset({
            apartmentId: undefined,
            reservationForSomeoneElse: false,
        });
    };

    const leftDays = apartmentDetailsData
        ? apartmentDetailsData.maxDays - apartmentDetailsData.usedDays
        : 0;

    const reservationDays =
        watch('checkOutDate') && watch('checkInDate')
            ? differenceInDays(watch('checkOutDate'), watch('checkInDate')) + 1
            : 0;
    const restDays = leftDays - reservationDays;

    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    const postButtons = (
        <>
            <Button type="button" variant="outline" onClick={onClose}>
                {t('elements.buttons.cancel')}
            </Button>
            <Button type="submit" onClick={handleSubmit(handleFormSubmit)}>
                {t('elements.buttons.create')}
            </Button>
        </>
    );

    const editButtons = (
        <>
            <Button type="button" variant="outline" onClick={onClose}>
                {t('elements.buttons.cancel')}
            </Button>
            <Button type="submit" onClick={handleSubmit(handleFormSubmit)}>
                {t('elements.buttons.saveChanges')}
            </Button>
        </>
    );

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent>
                <DialogHeader>
                    <DialogDescription />
                    <DialogTitle>
                        {!reservationId
                            ? t('main.reservations.components.create.title')
                            : t('main.reservations.components.edit.title')}
                        <DialogClose
                            className={cn(
                                'ml-auto cursor-pointer',
                                isMobile && 'h-11 p-3 border border-popover-100 rounded-full',
                            )}
                        >
                            <Cancel_01_Icon />
                        </DialogClose>
                    </DialogTitle>
                </DialogHeader>
                <div className="flex flex-col gap-4">
                    <ApartmentsLookup
                        fieldName={'apartmentId'}
                        control={control}
                        disabled={!!apartmentId || !!reservationId}
                        icon={DoorIcon}
                        label={t('elements.form.apartments.label2')}
                        placeholder={t('elements.form.apartments.placeholder')}
                    />
                    <div className="flex justify-between gap-4">
                        <EDatePicker
                            fieldName={'checkInDate'}
                            control={control}
                            fromDate={today}
                            label={t('elements.form.startDate.label2')}
                            placeholder={t('elements.form.startDate.placeholder')}
                        />
                        <EDatePicker
                            fieldName={'checkOutDate'}
                            control={control}
                            fromDate={tomorrow}
                            label={t('elements.form.endDate.label2')}
                            placeholder={t('elements.form.endDate.placeholder')}
                        />
                    </div>
                    {watch('checkInDate') && watch('checkOutDate') && (
                        <div className="p-3.5 flex gap-2 rounded-semi-xl border border-popover-100 bg-popover-10">
                            <InformationIconSolid className="size-4 shrink-0" />
                            <p className="text-xs font-medium text-popover-800">
                                {t('main.reservations.components.create.form.leftDays.part1')}{' '}
                                <span className="text-popover-1000">{leftDays}</span>{' '}
                                {t('main.reservations.components.create.form.leftDays.part2')}{' '}
                                <span className="text-popover-1000">{restDays}</span>{' '}
                                {t('main.reservations.components.create.form.leftDays.part3')}
                            </p>
                        </div>
                    )}
                    <div>
                        <ECheckboxWithTextInput
                            control={control}
                            fieldName={'reservationForSomeoneElse'}
                            text={t('main.reservations.components.create.form.forSomeoneElse')}
                            onClick={() => setValue('guestFullName', '')}
                        />
                        <div
                            className={cn(
                                'transition-all duration-150 ease-in-out',
                                !watch('reservationForSomeoneElse')
                                    ? 'max-h-0 opacity-0'
                                    : 'max-h-screen mt-4 opacity-100',
                            )}
                        >
                            <ETextInput
                                control={control}
                                fieldName={'guestFullName'}
                                label={t(
                                    'main.reservations.components.create.form.guestFullName.label',
                                )}
                                placeholder={t(
                                    'main.reservations.components.create.form.guestFullName.placeholder',
                                )}
                                className={cn(
                                    'transition-all duration-300 ease-in-out',
                                    !watch('reservationForSomeoneElse') ? 'hidden' : '',
                                )}
                            />
                        </div>
                    </div>
                    <ETextAreaInput
                        control={control}
                        fieldName={'remarks'}
                        optional={true}
                        label={t('elements.form.remarks.label')}
                        placeholder={t('elements.form.remarks.placeholder')}
                    />
                </div>
                <DialogFooter className={cn('justify-end', isMobile && 'mt-auto')}>
                    {!reservationId ? postButtons : editButtons}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default ReservationManage;
